import { z } from 'zod';

const moneyDto = z.object({
  amount: z.number(),
  currency: z.enum(['GEL', 'USD', 'EUR']),
});

const priceBreakdownDto = z.object({
  subtotal: moneyDto,
  discount: moneyDto,
  shipping: moneyDto,
  tax: moneyDto,
  total: moneyDto,
});

const postalAddressDto = z.object({
  recipient_name: z.string(),
  phone: z.string(),
  country: z.string(),
  city: z.string(),
  street: z.string(),
  apartment: z.string().nullable().default(null),
  postal_code: z.string().nullable().default(null),
  notes: z.string().nullable().default(null),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
});

const paymentMethodKindDto = z.enum([
  'cod',
  'card',
  'bog',
  'bog-card',
  'bog-apple-pay',
  'bog-google-pay',
  'bog-mobile-bank',
  'bog-loyalty',
  'bog-installment',
  'bog-bnpl',
  'tbc',
  'tbc-installment',
  'space',
  'tera',
]);

const paymentMethodDto = z.object({
  kind: paymentMethodKindDto,
  display_name: z.string(),
  last_four: z.string().nullable().default(null),
});

const orderItemDto = z.object({
  product_id: z.union([z.string(), z.number()]).transform(String),
  title: z.string(),
  unit_price: moneyDto,
  quantity: z.number().int().min(0),
  line_total: moneyDto,
});

const orderStatusDto = z.enum([
  'pending',
  'awaiting-payment',
  'paid',
  'processing',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
]);

export const orderDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  number: z.string(),
  user_id: z.union([z.string(), z.number()]).transform(String).nullable().default(null),
  customer_email: z.string(),
  status: orderStatusDto,
  items: z.array(orderItemDto).default([]),
  totals: priceBreakdownDto,
  promotions: z.array(z.unknown()).default([]),
  shipping_address: postalAddressDto,
  billing_address: postalAddressDto.nullable().default(null),
  payment_method: paymentMethodDto,
  placed_at: z.string(),
  updated_at: z.string(),
});

export const placeOrderResponseDto = z.object({
  order: orderDto,
  redirect_url: z.string().nullable().default(null),
  status_token: z.string().regex(/^[a-f0-9]{64}$/),
});

export const publicOrderStatusResponseDto = z.object({
  data: z.object({
    meta_purchase: z.record(z.unknown()).nullable().optional(),
    id: z.union([z.string(), z.number()]).transform(String),
    status: orderStatusDto,
    payment_method: z.enum(['cod', 'online']),
    buy_now: z.object({ product_id: z.string(), quantity: z.number().int().positive() }).nullable().default(null),
    value: z.number().nonnegative(),
    currency: z.literal('GEL'),
    shipping: z.number().nonnegative(),
    customer: z.object({ name: z.string(), email: z.string(), phone: z.string() }),
    delivery: z.object({ city: z.string(), address: z.string() }),
    items: z.array(
      z.object({
        item_id: z.string(),
        item_name: z.string(),
        price: z.number().nonnegative(),
        quantity: z.number().int().positive(),
      }),
    ),
    updated_at: z.string().nullable(),
  }),
});

export const shippingOptionDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  label: z.string(),
  price: moneyDto,
});

export const shippingOptionsResponseDto = z.object({
  data: z.array(shippingOptionDto),
});

const savedPaymentMethodDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  provider: z.string(),
  card_type: z.string().nullable().default(null),
  masked_card: z.string().nullable().default(null),
});

export const checkoutPaymentOptionsResponseDto = z.object({
  data: z.object({
    bog_sdk_url: z.string().url().nullable().default(null),
    contact: z
      .object({
        name: z.string(),
        email: z.string(),
        phone: z.string(),
      })
      .nullable()
      .default(null),
    saved_shipping: z
      .object({
        city_id: z.union([z.string(), z.number()]).transform(String).nullable().default(null),
        address: z.string().default(''),
        notes: z.string().default(''),
        phone: z.string().default(''),
        latitude: z.number().nullable().default(null),
        longitude: z.number().nullable().default(null),
      })
      .nullable()
      .default(null),
    saved_payment_methods: z.array(savedPaymentMethodDto).default([]),
  }),
});

export type OrderDto = z.infer<typeof orderDto>;
export type PlaceOrderResponseDto = z.infer<typeof placeOrderResponseDto>;
export type ShippingOptionDto = z.infer<typeof shippingOptionDto>;
export type CheckoutPaymentOptionsResponseDto = z.infer<typeof checkoutPaymentOptionsResponseDto>;
